/**
 * Decision and Repeat v2 (Ram, 2026-10-23) — a step's condition as text, pure.
 *
 * A condition is stored one of two ways, and both stay readable:
 *   - `rawExpression` — the C the keyboard (or this editor) wrote; it wins in
 *     codegen whenever it is set;
 *   - guided `conditions[]` rows — shown as exactly the C codegen emits for them
 *     (guidedConditionC, codeGenerator.js), so the chips say what runs.
 *
 * Nothing here writes until the student edits: `writeCondition` is the one
 * write, and it says the same thing the keyboard says (`conditionMode: 'raw'`)
 * with the guided rows cleared so codegen cannot fall back to them.
 *
 * The words ("marks is at least 75, and marks is less than 90") and the maths
 * ("marks ≥ 75 and marks < 90") come from the expression keyboard's engine, so
 * a card, a pill and the editor all read a condition the same way. Neither has
 * any C in it: no ==, &&, || or !.
 */
import { guidedConditionC } from './codeGenerator';
import {
  createEnv, tokenize, serialize, analyse,
} from '@/components/visual-flow-builder/ExpressionKeyboard/engine';

const trimmed = (s) => String(s == null ? '' : s).trim();

/** The C a Decision path checks: its raw condition, else its guided rows as codegen writes them, else ''. */
export function pathConditionC(outcome, resources = []) {
  if (!outcome) return '';
  const raw = trimmed(outcome.rawExpression);
  if (raw) return raw;
  return guidedConditionC(outcome, resources, { kind: 'decision' });
}

/** The C a Loop checks before each pass (its raw condition, else its guided rows), or ''. */
export function loopConditionC(node, resources = []) {
  if (!node) return '';
  const raw = trimmed(node.rawExpression);
  if (raw) return raw;
  return guidedConditionC(node, resources, { kind: 'loop' });
}

/** Is this path checked by guided rows (nothing written yet by a v2 edit)? */
export function isGuidedOnly(holder) {
  return !trimmed(holder && holder.rawExpression)
    && (holder && Array.isArray(holder.conditions) ? holder.conditions : [])
      .some((c) => c && (trimmed(c.resource) || trimmed(c.resourceFullPath)));
}

/**
 * The one write: an edited condition is stored the way the keyboard stores it.
 * Clearing it clears the guided rows too, or codegen would quietly use them.
 */
export function writeCondition(holder, c) {
  return {
    ...holder,
    rawExpression: c,
    rawExpressionAst: null,
    conditionMode: 'raw',
    conditions: [],
  };
}

/**
 * The env, plus every name the text uses that it does not know (an outer
 * loop's counter, a name from another file) as a plain value — so a condition
 * can always be READ in words. Only for reading: the editor's own checks use
 * the real env.
 */
export function readingEnv(c, env = createEnv()) {
  const base = env || createEnv();
  let toks = [];
  try { toks = tokenize(String(c || ''), base); } catch (e) { return base; }
  const known = (n) => (base.vars && base.vars.has(n)) || (base.consts && base.consts.has(n));
  const extra = [...new Set(toks.filter((t) => t.k === 'var' && !known(t.v)).map((t) => t.v))];
  const fns = toks.filter((t) => t.k === 'fn' && !(base.functions && Object.prototype.hasOwnProperty.call(base.functions, t.v))).map((t) => t.v);
  if (!extra.length && !fns.length) return base;
  const functions = { ...(base.functions || {}) };
  fns.forEach((f) => { functions[f] = { params: [], minParams: 0, variadic: true }; });
  return createEnv({
    variables: [...(base.variables || []), ...extra.map((name) => ({ name, type: 'int' }))],
    constants: (base.constants || []).filter((x) => x && !x.builtin),
    functions,
    types: [...(base.types || [])],
  });
}

/** The condition as the keys show it: `marks ≥ 75 and marks < 90`. Never C. */
export function conditionMath(c, env = createEnv()) {
  const s = trimmed(c);
  if (!s) return '';
  try {
    const toks = tokenize(s, env);
    if (toks.some((t) => t.k === 'bad' || t.open)) return s;
    return serialize(toks).text;
  } catch (e) {
    return s;
  }
}

/** The condition in words: `marks is at least 75, and marks is less than 90` (falls back to the maths). */
export function conditionWords(c, env = createEnv()) {
  const s = trimmed(c);
  if (!s) return '';
  try {
    const e2 = readingEnv(s, env);
    const a = analyse(tokenize(s, e2), e2);
    if (a.tree && a.words) return a.words;
  } catch (e) { /* fall back */ }
  return conditionMath(s, env);
}

const clip = (s, max) => (s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s);

// Names a path is created with or that the old editor gave it: they are not
// the student's, so they are not shown as a name.
const DEFAULT_PATH_NAMES = /^(true|false|yes|no|default|default outcome|otherwise|else|else if|if|outcome(\s*\d+)?|path(\s*\d+)?)$/i;

/** A path's own name, or '' when it only has a default one. */
export function pathName(outcome) {
  const l = trimmed(outcome && outcome.label);
  if (!l || DEFAULT_PATH_NAMES.test(l)) return '';
  return l;
}

/** A condition's text with spacing and spelling ironed out, to compare two of them. */
const sameForm = (t) => trimmed(t).toLowerCase()
  .replace(/≥/g, '>=').replace(/≤/g, '<=').replace(/≠/g, '!=').replace(/×/g, '*').replace(/÷/g, '/')
  .replace(/==/g, '=')
  .replace(/\band\b/g, '&&').replace(/\bor\b/g, '||')
  .replace(/\s+/g, '')
  .replace(/^\((.*)\)$/, '$1');

/** Does a path's name only repeat its condition ("marks>=60" on `marks >= 60`)? Then it is not shown. */
export function nameRepeatsCheck(name, c, env = createEnv()) {
  const n = sameForm(name);
  if (!n) return false;
  return n === sameForm(c) || n === sameForm(conditionMath(c, env));
}

export const PILL_MAX = 26;

/**
 * What the canvas pill on a Decision path says (v2): the path's name, else a
 * short version of its condition in words, else "Yes" / "Else" (the algorithm's word, Ram 2026-10-29).
 */
export function pathPillText(outcome, resources = [], env = createEnv()) {
  const name = pathName(outcome);
  if (name) return name;
  if (outcome && outcome.isDefault) return 'Else';
  const c = pathConditionC(outcome, resources);
  if (!c) return 'Yes';
  const words = conditionWords(c, env);
  if (words.length <= PILL_MAX) return words;
  const math = conditionMath(c, env);
  return clip(math.length <= words.length ? math : words, PILL_MAX);
}
